import { Box, Checkbox, Grid } from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import React, { useContext, useEffect, useRef, useState, ReactNode } from "react";
import {
  ConfigurationParameterValue,
  SelectiveExportOptions,
} from "../../../../api/models/test-series/TestSeriesExcelExport";
import TestSeriesExcelExportApi from "../../../../api/services/TestSeriesExcelExportApi";
import CustomLoading from "../../../../components/Alert/CustomLoading";
import CustomButton from "../../../../components/Button/CustomButton";
import ConfirmationDialogContext from "../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import CustomDialog from "../../../../components/Dialog/CustomDialog";
import InfoBox from "../../../../components/InfoBox";
import CustomDropdownMultiple from "../../../../components/Input/CustomDropdownMultiple";
import { ErrorSnackbarContext } from "../../../../contexts/ErrorSnackbarContext";
import downloadFile from "../../../../functions/downloadFile.function";
import useEnv from "../../../../hooks/env";
import { HttpErrorType } from "../../../../models/HttpErrorType";
import { tesaDarkRedError } from "../../../../styles/colors";
import { ExcelExportInnerFormikProps } from "./interfaces/ExcelExportInnerFormikProps";
import useStyles from "./TestSeriesExcelExportDialogStyle";
import validationSchema from "./utils/TestSeriesExcelExportValidationSchema";

const initialOptions = {
  repetition: true,
  meanValue: true,
  standardDeviation: true,
  targetValue: true,
  testMethodOptions: [],
  firstSubstrateOptions: [],
  secondSubstrateOptions: [],
  productOptions: [],
  buildingActionOptions: [],
  specimenTreatmentOptions: [],
  conditionOptions: [],
};

export interface ExcelExportDialogProps {
  testRequest: Record<string, any>;
  open: boolean;
  handleClose: (v: boolean) => void;
}

const TestSeriesExcelExportDialog: React.FC<ExcelExportDialogProps> = (props) => {
  const { api } = useEnv();
  const excelExportApi = new TestSeriesExcelExportApi(api);

  const formRef = useRef<FormikProps<ExcelExportInnerFormikProps>>(null);

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);

  const classes = useStyles();

  const { open, handleClose, testRequest } = props;

  const [loading, setLoading] = useState<boolean>(true);
  const [excelExportOptions, setExcelExportOptions] = useState<SelectiveExportOptions>(initialOptions);

  const showExcelExportConfirmationDialog = () => {
    if (formRef && formRef.current && formRef.current.dirty) {
      showConfirmationDialog({
        headline: "You have unsaved changes",
        text: "If you continue, your current changes to the excel export will be discarded.",
        confirmText: "Save",
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      }).then((choice) => {
        if (choice === "DEFAULT") {
          formRef?.current?.handleSubmit();
        } else {
          formRef?.current?.handleReset();
        }
        handleClose(false);
      });
    } else {
      handleClose(false);
    }
  };

  const findIdsForSelectedOptions = (names: string[], options: ConfigurationParameterValue[]) => {
    const ids: number[] = [];
    names?.forEach((name) => {
      const match = options.find((option) => option.referenceName === name);
      if (match) {
        ids.push(match.referenceId);
      }
    });
    return ids;
  };

  const handleOnSubmit = (values: ExcelExportInnerFormikProps) => {
    const testMethods = findIdsForSelectedOptions(values.testMethodOptions, excelExportOptions.testMethodOptions);
    const firstSubstrates = findIdsForSelectedOptions(
      values.firstSubstrateOptions,
      excelExportOptions.firstSubstrateOptions,
    );

    const secondSubstrates = findIdsForSelectedOptions(
      values.secondSubstrateOptions,
      excelExportOptions.secondSubstrateOptions,
    );

    const products = findIdsForSelectedOptions(values.productOptions, excelExportOptions.productOptions);

    const buildingActions = findIdsForSelectedOptions(
      values.buildingActionOptions,
      excelExportOptions.buildingActionOptions,
    );

    const specimenTreatments = findIdsForSelectedOptions(
      values.specimenTreatmentOptions,
      excelExportOptions.specimenTreatmentOptions,
    );

    const conditions = findIdsForSelectedOptions(values.conditionOptions, excelExportOptions.conditionOptions);

    const { repetition, meanValue, standardDeviation, targetValue } = values;

    excelExportApi
      .exportSelectionOfTestRequest(
        testRequest.requestId,
        repetition,
        meanValue,
        standardDeviation,
        targetValue,
        testMethods,
        firstSubstrates,
        secondSubstrates,
        products,
        buildingActions,
        specimenTreatments,
        conditions,
      )
      .then((response) => downloadFile(response, `${testRequest.fullTitle}_selected_results.xls`))
      .catch((error: HttpErrorType) => {
        if (error.response && error.response.data) {
          const data = new Uint8Array(error.response.data as ArrayBuffer);
          const textDecoder = new TextDecoder();
          const errorMessageObjStr = textDecoder.decode(data);
          const errorMessageObj = JSON.parse(errorMessageObjStr);
          handleHttpError(new Error(errorMessageObj.message));
        } else {
          handleHttpError(error);
        }
      })
      .finally(() => handleClose(true));
  };

  useEffect(() => {
    excelExportApi
      .getSelectiveExportOptions(testRequest.requestId)
      .then(({ data }) => {
        setExcelExportOptions(data);
      })
      .catch(handleHttpError)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <CustomLoading />;
  }

  return (
    <CustomDialog fullWidth maxWidth="sm" open={open} onClose={showExcelExportConfirmationDialog}>
      <div className={classes.header}>
        <h2>Excel export parameters</h2>
        <p>Select the test series parameters to be visible in excel sheet.</p>
      </div>
      <Formik
        innerRef={formRef}
        enableReinitialize
        onSubmit={handleOnSubmit}
        initialValues={initialOptions}
        validationSchema={validationSchema}
      >
        {(formikProps: FormikProps<ExcelExportInnerFormikProps>): ReactNode => {
          const { errors, dirty, handleChange, isSubmitting, values } = formikProps;

          const {
            testMethodOptions,
            firstSubstrateOptions,
            secondSubstrateOptions,
            productOptions,
            buildingActionOptions,
            specimenTreatmentOptions,
            conditionOptions,
          } = excelExportOptions;

          return (
            <Form>
              <Grid container direction="column" spacing={3}>
                <Grid item className={classes.dropdownItems}>
                  <div className={classes.labeledCheckbox}>
                    <Checkbox
                      name="repetition"
                      size="small"
                      className={classes.checkbox}
                      checked={values.repetition}
                      onChange={handleChange}
                    />
                    <p>Repetition</p>
                  </div>
                  <div className={classes.labeledCheckbox}>
                    <Checkbox
                      name="targetValue"
                      size="small"
                      className={classes.checkbox}
                      checked={values.targetValue}
                      onChange={handleChange}
                    />
                    <p>Target value</p>
                  </div>
                  <div className={classes.labeledCheckbox}>
                    <Checkbox
                      name="meanValue"
                      size="small"
                      className={classes.checkbox}
                      checked={values.meanValue}
                      onChange={handleChange}
                    />
                    <p>Mean value</p>
                    <InfoBox text="The mean value only applies to numerical values" />
                  </div>
                  <div className={classes.labeledCheckbox}>
                    <Checkbox
                      name="standardDeviation"
                      size="small"
                      className={classes.checkbox}
                      checked={values.standardDeviation}
                      onChange={handleChange}
                    />
                    <p>Standard deviation</p>
                    <InfoBox text="The standard deviation only applies to numerical values" />
                  </div>
                  <Box className={classes.error}>{errors && (errors as { atLeastOne: string })?.atLeastOne}</Box>
                  {testMethodOptions.length > 0 && (
                    <CustomDropdownMultiple
                      chips
                      name="testMethodOptions"
                      labelText="Test method options"
                      options={testMethodOptions.map((option) => option.referenceName)}
                      value={values.testMethodOptions}
                      onChange={handleChange}
                    />
                  )}
                  {productOptions.length > 0 && (
                    <CustomDropdownMultiple
                      chips
                      name="productOptions"
                      labelText="Product options"
                      options={productOptions.map((option) => option.referenceName)}
                      value={values.productOptions}
                      onChange={handleChange}
                    />
                  )}
                  {firstSubstrateOptions.length > 0 && (
                    <CustomDropdownMultiple
                      chips
                      name="firstSubstrateOptions"
                      labelText="Substrate options"
                      options={firstSubstrateOptions.map((option) => option.referenceName)}
                      value={values.firstSubstrateOptions}
                      onChange={handleChange}
                    />
                  )}
                  {secondSubstrateOptions.length > 0 && (
                    <CustomDropdownMultiple
                      chips
                      name="secondSubstrateOptions"
                      labelText="Second substrate options"
                      options={secondSubstrateOptions.map((option) => option.referenceName)}
                      value={values.secondSubstrateOptions}
                      onChange={handleChange}
                    />
                  )}
                  {buildingActionOptions.length > 0 && (
                    <CustomDropdownMultiple
                      chips
                      name="buildingActionOptions"
                      labelText="Building action options"
                      options={buildingActionOptions.map((option) => option.referenceName)}
                      value={values.buildingActionOptions}
                      onChange={handleChange}
                    />
                  )}
                  {specimenTreatmentOptions.length > 0 && (
                    <CustomDropdownMultiple
                      chips
                      name="specimenTreatmentOptions"
                      labelText="Specimen treatment options"
                      options={specimenTreatmentOptions.map((option) => option.referenceName)}
                      value={values.specimenTreatmentOptions}
                      onChange={handleChange}
                    />
                  )}
                  {conditionOptions.length > 0 && (
                    <CustomDropdownMultiple
                      chips
                      name="conditionOptions"
                      labelText="Condition options"
                      options={conditionOptions.map((option) => option.referenceName)}
                      value={values.conditionOptions}
                      onChange={handleChange}
                    />
                  )}
                </Grid>
                <Grid container item direction="row" justify="flex-end">
                  <Box marginLeft={3}>
                    <CustomButton disabled={isSubmitting || !dirty} text="Revert" type="reset" />
                  </Box>
                  <Box marginLeft={3}>
                    <CustomButton disabled={isSubmitting} primary text="Export" type="submit" />
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default TestSeriesExcelExportDialog;
